import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { IndianRupee, Calendar, Briefcase } from 'lucide-react';

const Projects = () => {
  // Helper function to extract the end year from duration string
  const getEndYear = (duration) => {
    const years = duration.match(/\d{4}/g);
    if (years && years.length > 0) {
      return parseInt(years[years.length - 1]); // Get the last year
    }
    return 0;
  };

  const sponsoredProjects = [
    {
      title: 'Manufacturing and testing of fibre reinforced composites',
      agency: 'IIT-Guwahati',
      value: '5.00',
      duration: '2017-2019',
      status: 'Completed',
      role: 'PI',
    },
    {
      title: 'An affordable lower limb prosthesis with polycentric knee joint, dynamic ankle joint and suction-suspension socket system',
      agency: 'DST-SERB (IMPRINT)',
      value: '73.37',
      duration: '2018-2021',
      status: 'Completed',
      role: 'Co-PI',
    },
    {
      title: 'Walking aid device for disabled patients',
      agency: 'NewGen IEDC',
      value: '2.50',
      duration: '2018-2019',
      status: 'Completed',
      role: 'Mentor',
    },
    {
      title: 'Computational and Experimental study of damage and failure in carbon/glass fiber reinforced composite materials',
      agency: 'DST-SERB',
      value: '49.59',
      duration: '2019-2022',
      status: 'Completed',
      role: 'PI',
    },
    {
      title: 'Functionality Enhancement through Design and Development of Advanced Finite Element Algorithms for STR TOOLS',
      agency: 'DST-SERB and VSSC, ISRO',
      value: '99.00',
      duration: '2019-2022',
      status: 'Completed',
      role: 'Co-PI',
    },
    {
      title: 'Versatile standing cum sitting device - VSD',
      agency: 'NewGen IEDC',
      value: '2.50',
      duration: '2021-2022',
      status: 'Completed',
      role: 'Mentor',
    },
    {
      title: 'Development of intelligent Vision System for Underwater Drones',
      agency: 'NewGen IEDC',
      value: '2.50',
      duration: '2021-2022',
      status: 'Completed',
      role: 'Mentor',
    },
    {
      title: 'Waste Disposal (Recycling) of Fiber reinforced Polymer Composites',
      agency: 'TATA Steel (MaterialNEXT)',
      value: '0.60',
      duration: '2022',
      status: 'Completed',
      role: 'Mentor',
    },
    {
      title: 'Recycling of Fiber (Carbon & Glass) reinforced Polymer Composites',
      agency: 'TATA Steel',
      value: '24.00',
      duration: '2022-24',
      status: 'Completed',
      role: 'Co-PI',
    },
    {
      title: 'Development of an Autonomous System for an Underwater Vehicle',
      agency: 'Technology Innovation Hub, IITG',
      value: '49.90',
      duration: '2022-24',
      status: 'Completed',
      role: 'Co-PI',
    },
    {
      title: 'Manpack Bridges',
      agency: 'Technology Innovation Hub, IITG TIDF',
      value: '6.40',
      duration: '2025-26',
      status: 'Ongoing',
      role: 'PI',
    },
    {
      title: 'Design, Development and Testing of Meta-Sandwich Structures for Impact Applications',
      agency: 'IIT Palakkad',
      value: '34.00',
      duration: '2023-25',
      status: 'Ongoing',
      role: 'PI',
    },
    {
      title: 'Composite 3D Printer for Manufacturing Sandwich Composites',
      agency: 'IIT Palakkad',
      value: '5.00',
      duration: '2023-25',
      status: 'Completed',
      role: 'PI',
    },
    {
      title: 'Design and mechanical testing of meta-sandwich structures with FSS PCB inserts',
      agency: 'DRDO, CARS',
      value: '9.82',
      duration: 'Ongoing',
      status: 'Ongoing',
      role: 'PI',
    },
  ];

  // Sort projects by end year (newest first)
  const sortedSponsoredProjects = [...sponsoredProjects].sort((a, b) => getEndYear(b.duration) - getEndYear(a.duration));

  const consultancyProjects = [
    {
      title: 'Training on Creep and Thermal Fatigue',
      agency: 'FCA Engineering Pvt. India Ltd.',
      value: '2.30',
      duration: '2018',
      status: 'Completed',
    },
    {
      title: 'Yield Strength, Coated Thickness & Base Metal Thickness testing',
      agency: 'M/s CADMETRIC Consulting',
      value: '0.47',
      duration: '2021',
      status: 'Completed',
    },
    {
      title: 'Advanced Predictive Methodology for Fatigue Life Calculation',
      agency: 'SABIC',
      value: '11.49',
      duration: '2022-23',
      status: 'Completed',
    },
    {
      title: 'AI/ML model for quality assessment of wood logs',
      agency: 'TreeKisan',
      value: '0.47',
      duration: '2023',
      status: 'Completed',
    },
    {
      title: 'Consultancy in Tribology, Inspection, Composites and Additive Manufacturing',
      agency: 'GE Renewable Energy',
      value: '4.00',
      duration: '2024',
      status: 'Completed',
    },
    {
      title: 'Testing of Aluminum composite material samples as per IOCL specification',
      agency: 'IOCL',
      value: '5.87',
      duration: '2025',
      status: 'Completed',
    },
  ];

  // Sort consultancy projects by year (newest first)
  const sortedConsultancyProjects = [...consultancyProjects].sort((a, b) => getEndYear(b.duration) - getEndYear(a.duration));

  const ProjectCard = ({ project, showRole = false }) => (
    <Card className="hover-lift border-2 border-transparent hover:border-blue-200 interactive-card">
      <div className={`h-2 bg-gradient-to-r ${project.status === 'Ongoing' ? 'from-green-500 to-emerald-500' : 'from-blue-600 to-cyan-500'}`}></div>
      <CardHeader className="bg-gradient-to-br from-slate-50 to-blue-50">
        <CardTitle className="text-xl bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-3">
          {project.title}
        </CardTitle>
        <div className="flex flex-wrap gap-2">
          <Badge
            variant={project.status === 'Completed' ? 'secondary' : 'default'}
            className={project.status === 'Ongoing' ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600' : 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'}
          >
            {project.status}
          </Badge>
          {showRole && project.role && (
            <Badge variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
              {project.role}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="space-y-3 text-slate-700">
          <div className="flex items-center gap-2 bg-blue-50 p-3 rounded-lg">
            <Briefcase size={18} className="text-blue-600" />
            <span className="font-semibold">{project.agency}</span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2 bg-cyan-50 px-3 py-2 rounded-lg">
              <IndianRupee size={16} className="text-cyan-600" />
              <span className="font-bold text-cyan-700">{project.value} Lakhs</span>
            </div>
            <div className="flex items-center gap-2 bg-purple-50 px-3 py-2 rounded-lg">
              <Calendar size={16} className="text-purple-600" />
              <span className="font-semibold text-purple-700">{project.duration}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <section id="projects" className="section-container bg-gradient-to-br from-white via-cyan-50 to-purple-50 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-blue-200 to-cyan-200 rounded-full filter blur-3xl opacity-20 animate-float"></div>
      
      <h2 className="section-title">Research Projects</h2>
      
      <div className="w-full relative z-10">
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <Card className="border-2 border-transparent hover:border-blue-200 overflow-hidden group hover-lift">
            <div className="h-2 bg-gradient-to-r from-blue-600 to-cyan-500"></div>
            <CardContent className="p-8 text-center bg-gradient-to-br from-blue-50 to-cyan-50 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-cyan-500 opacity-0 group-hover:opacity-10 transition-opacity duration-500"></div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-2 relative z-10">Sponsored Projects</h3>
              <div className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-2 relative z-10">14</div>
              <p className="text-slate-600 relative z-10">Sponsored research projects</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-transparent hover:border-purple-200 overflow-hidden group hover-lift">
            <div className="h-2 bg-gradient-to-r from-purple-600 to-pink-500"></div>
            <CardContent className="p-8 text-center bg-gradient-to-br from-purple-50 to-pink-50 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-pink-500 opacity-0 group-hover:opacity-10 transition-opacity duration-500"></div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2 relative z-10">Consultancy Projects</h3>
              <div className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2 relative z-10">6</div>
              <p className="text-slate-600 relative z-10">Industry partnerships</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="sponsored" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8 bg-white shadow-lg">
            <TabsTrigger value="sponsored" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-cyan-500 data-[state=active]:text-white">Sponsored Research</TabsTrigger>
            <TabsTrigger value="consultancy" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-pink-500 data-[state=active]:text-white">Consultancy</TabsTrigger>
          </TabsList>

          <TabsContent value="sponsored">
            <div className="grid gap-6">
              {sortedSponsoredProjects.map((project, index) => (
                <ProjectCard key={index} project={project} showRole={true} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="consultancy">
            <div className="grid gap-6">
              {sortedConsultancyProjects.map((project, index) => (
                <ProjectCard key={index} project={project} />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default Projects;